import { FlexBox, Icon, Text } from '@dixa/lib-shared-habla';
import { useMemo } from 'react';

import { ToolExecutionData, ToolOutput } from 'apis/auditLogs';
import { filesApi } from 'apis/files';
import { CodeHighlight } from 'components/codeHighlight';

import { AutomationTaskExecutionActivity } from '../chatbotToolCalled/automationExecutionActivity';
import styles from '../logEntry/logEntry.module.scss';

type ThinkingEntryRowProps = {
  output: ToolOutput;
  execution_data?: ToolExecutionData;
};

export const ThinkingEntryRow = ({
  output,
  execution_data,
}: ThinkingEntryRowProps): JSX.Element | null => {
  const {
    content: outputContent,
    isLoading,
    isError,
  } = filesApi.useReadFileQuery(
    { path: output?.s3_path || '' },
    {
      skip: !output?.s3_path,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        content: data?.data?.content,
      }),
    },
  );

  const outputResult = useMemo(() => {
    if (isError) {
      return (
        <Text.Paragraph variant="3" subdued>
          Error loading output
        </Text.Paragraph>
      );
    }
    if (isLoading || !outputContent) {
      return (
        <Text.Paragraph variant="3" subdued>
          Loading output...
        </Text.Paragraph>
      );
    }
    return (
      <CodeHighlight
        languages={['json']}
        style={{
          overflow: 'auto',
          maxHeight: '500px',
        }}
      >
        {typeof outputContent === 'string'
          ? outputContent
          : JSON.stringify(outputContent, null, 2)}
      </CodeHighlight>
    );
  }, [isError, isLoading, outputContent]);

  if (!output?.s3_path) {
    return null;
  }

  return (
    <FlexBox flexDirection="column" gap="200" width="100%">
      <FlexBox flexDirection="row" alignItems="center" gap="200">
        <div className={styles.iconContainer}>
          <Icon name="brain" size={16} />
        </div>
        <Text.Paragraph variant="4" subdued>
          AI agent thinking
        </Text.Paragraph>
      </FlexBox>
      <FlexBox flexDirection="column" gap="200" margin="0 0 0 500">
        {outputResult}
        {execution_data?.type === 'automation' && execution_data.automation && (
          <AutomationTaskExecutionActivity
            executionData={execution_data.automation}
          ></AutomationTaskExecutionActivity>
        )}
      </FlexBox>
    </FlexBox>
  );
};
